/**
 * Utility for analyzing file size of survey sections
 * Helps identify base64 photo bloat and large data fields
 */

export interface PhotoFieldAnalysis {
  field: string;
  count: number;
  totalSize: number;
  base64Count: number;
  base64Size: number;
  cdnCount: number;
  cdnSize: number;
  photos: Array<{
    displayId: string;
    hasBase64: boolean;
    hasCDN: boolean;
    size: number;
    uploading: boolean;
  }>;
}

export interface FileSizeAnalysisResult {
  totalSize: number;
  nonPhotoSize: number;
  photoSize: number;
  totalPhotos: number;
  totalBase64Photos: number;
  totalBase64Size: number;
  totalCDNPhotos: number;
  totalCDNSize: number;
  photoAnalysis: PhotoFieldAnalysis[];
  nonPhotoBreakdown: Array<{ field: string; size: number }>;
}

/**
 * Calculate size of an object in bytes
 */
export function getSize(obj: any): number {
  return new Blob([JSON.stringify(obj)]).size;
}

/**
 * Format bytes to human-readable string
 */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
}

/**
 * Analyze file size of a survey section with photo fields
 */
export function analyzeSectionFileSize(
  sectionName: string,
  sectionData: any,
  photoFields: string[]
): FileSizeAnalysisResult {
  if (!sectionData) {
    console.log(`📊 [${sectionName.toUpperCase()}] No data to analyze`);
    return {
      totalSize: 0,
      nonPhotoSize: 0,
      photoSize: 0,
      totalPhotos: 0,
      totalBase64Photos: 0,
      totalBase64Size: 0,
      totalCDNPhotos: 0,
      totalCDNSize: 0,
      photoAnalysis: [],
      nonPhotoBreakdown: []
    };
  }

  const photoAnalysis: PhotoFieldAnalysis[] = [];
  let totalPhotos = 0;
  let totalSize = 0;
  let totalBase64Photos = 0;
  let totalBase64Size = 0;
  let totalCDNPhotos = 0;
  let totalCDNSize = 0;

  // Analyze each photo field
  photoFields.forEach(fieldName => {
    const field = (sectionData as any)[fieldName];
    if (!field) return;

    const photos = Array.isArray(field) ? field : [field];
    const fieldAnalysis: PhotoFieldAnalysis = {
      field: fieldName,
      count: 0,
      totalSize: 0,
      base64Count: 0,
      base64Size: 0,
      cdnCount: 0,
      cdnSize: 0,
      photos: []
    };

    photos.forEach((photo: any) => {
      if (!photo) return;

      const photoSize = getSize(photo);
      const hasBase64 = !!(photo.dataUrl || photo.base64String);
      const hasCDN = !!(photo.url && photo.url.startsWith('http'));

      fieldAnalysis.count++;
      fieldAnalysis.totalSize += photoSize;
      totalPhotos++;
      totalSize += photoSize;

      if (hasBase64) {
        fieldAnalysis.base64Count++;
        fieldAnalysis.base64Size += photoSize;
        totalBase64Photos++;
        totalBase64Size += photoSize;
      }

      if (hasCDN) {
        fieldAnalysis.cdnCount++;
        fieldAnalysis.cdnSize += photoSize;
        totalCDNPhotos++;
        totalCDNSize += photoSize;
      }

      fieldAnalysis.photos.push({
        displayId: photo.displayId || photo.id || 'unknown',
        hasBase64,
        hasCDN,
        size: photoSize,
        uploading: photo.uploading || false
      });
    });

    if (fieldAnalysis.count > 0) {
      photoAnalysis.push(fieldAnalysis);
    }
  });

  // Calculate non-photo data size with detailed breakdown
  const dataWithoutPhotos = { ...sectionData };
  photoFields.forEach(field => {
    delete (dataWithoutPhotos as any)[field];
  });
  const nonPhotoSize = getSize(dataWithoutPhotos);

  // Analyze each non-photo field to find what's large
  const nonPhotoBreakdown: Array<{ field: string; size: number }> = [];
  Object.keys(dataWithoutPhotos).forEach(key => {
    const fieldValue = (dataWithoutPhotos as any)[key];
    const fieldSize = getSize(fieldValue);
    if (fieldSize > 1000) {
      // Only show fields > 1 KB
      nonPhotoBreakdown.push({ field: key, size: fieldSize });
    }
  });
  nonPhotoBreakdown.sort((a, b) => b.size - a.size);

  // Sort photo analysis by total size descending
  photoAnalysis.sort((a, b) => b.totalSize - a.totalSize);

  return {
    totalSize: totalSize + nonPhotoSize,
    nonPhotoSize,
    photoSize: totalSize,
    totalPhotos,
    totalBase64Photos,
    totalBase64Size,
    totalCDNPhotos,
    totalCDNSize,
    photoAnalysis,
    nonPhotoBreakdown
  };
}

/**
 * Log file size analysis results to console
 */
export function logFileSizeAnalysis(
  sectionName: string,
  result: FileSizeAnalysisResult,
  cleanupFunctionName?: string
): void {
  const {
    totalSize,
    nonPhotoSize,
    photoSize,
    totalPhotos,
    totalBase64Photos,
    totalBase64Size,
    totalCDNPhotos,
    totalCDNSize,
    photoAnalysis,
    nonPhotoBreakdown
  } = result;

  console.log('');
  console.log('📊 ========================================');
  console.log(`📊 [${sectionName.toUpperCase()}] FILE SIZE ANALYSIS`);
  console.log('📊 ========================================');
  console.log(`📊 Total Size: ${formatBytes(totalSize)}`);
  console.log(`📊 Non-Photo Data: ${formatBytes(nonPhotoSize)}`);

  // Show non-photo breakdown if unusually large
  if (nonPhotoSize > 10000 && nonPhotoBreakdown.length > 0) {
    console.warn(`   ⚠️ Non-photo data is unusually large! Breakdown:`);
    nonPhotoBreakdown.forEach(item => {
      console.warn(`   ├─ ${item.field}: ${formatBytes(item.size)}`);
    });
  }

  console.log(`📊 Photo Data: ${formatBytes(photoSize)}`);
  console.log('📊 ========================================');
  console.log(`📊 Total Photos: ${totalPhotos}`);
  console.log(`📊 Photos with Base64: ${totalBase64Photos} (${formatBytes(totalBase64Size)})`);
  console.log(`📊 Photos with CDN: ${totalCDNPhotos} (${formatBytes(totalCDNSize)})`);
  console.log('📊 ========================================');

  // Log each photo field
  photoAnalysis.forEach(field => {
    const emoji = field.base64Size > 10000 ? '🔴' : field.base64Size > 0 ? '🟡' : '🟢';
    console.log(`${emoji} ${field.field}: ${formatBytes(field.totalSize)} (${field.count} photos)`);
    console.log(`   ├─ Base64: ${field.base64Count} photos (${formatBytes(field.base64Size)})`);
    console.log(`   ├─ CDN: ${field.cdnCount} photos (${formatBytes(field.cdnSize)})`);

    // Show individual photos if field has base64
    if (field.base64Size > 0) {
      field.photos.forEach(photo => {
        if (photo.hasBase64) {
          const status = photo.uploading
            ? '⏳ uploading'
            : photo.hasCDN
            ? '⚠️ has CDN but still has base64'
            : '❌ base64 only';
          console.log(`   │  └─ ${photo.displayId.substring(0, 30)}: ${formatBytes(photo.size)} (${status})`);
        }
      });
    }
  });

  console.log('📊 ========================================');

  // Show recommendations
  if (totalBase64Size > 100000) {
    console.warn('⚠️ [RECOMMENDATION] Large base64 data detected!');
    console.warn(`   ${formatBytes(totalBase64Size)} could be saved by migrating to CDN`);

    // Check if photos have CDN URLs but still have base64
    const photosWithBothCDNAndBase64 = photoAnalysis.filter(f => f.base64Size > 0 && f.cdnCount > 0).length;

    if (photosWithBothCDNAndBase64 > 0) {
      console.warn('   ⚠️ Photos have CDN URLs but still have base64 data!');
      if (cleanupFunctionName) {
        console.warn(`   💡 Run cleanup: window.${cleanupFunctionName}()`);
      }
    } else {
      console.warn('   Run photo migration or wait for photos to finish uploading');
    }
  }

  if (totalBase64Photos > 0) {
    console.warn(`⚠️ [ACTION NEEDED] ${totalBase64Photos} photos still have base64 data`);
    console.warn('   These photos should be uploaded to CDN to reduce file size');
  }

  if (totalBase64Photos === 0 && totalCDNPhotos > 0) {
    console.log('✅ [EXCELLENT] All photos are on CDN, no base64 bloat!');
  }

  console.log('📊 ========================================');
  console.log('');
}
